import React, { useEffect, useRef, useState } from "react";

import axios from "axios";

import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";

import {
  Images,
  Image as ImageIcon,
  PlusCircle,
  PencilSquare,
  Trash,
  ArrowLeft,
  CheckCircleFill,
  XCircleFill,
  Search,
  X,
  Save,
  ArrowClockwise,
  EyeFill,
} from "react-bootstrap-icons";

import { useNavigate } from "react-router-dom";

import "./AdminCarousel.css";

/* ============================================================
   API
============================================================ */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   FORMULARIO VACÍO
============================================================ */

const formularioInicial = {
  titulo: "",
  descripcion: "",
  orden: 0,
  activo: true,
};

/* ============================================================
   COMPONENTE
============================================================ */

export default function AdminCarousel() {
  const navigate = useNavigate();

  const inputImagenRef = useRef(null);

  /* ============================================================
     ESTADOS
  ============================================================ */

  const [slides, setSlides] = useState([]);

  const [form, setForm] = useState(formularioInicial);

  const [imagen, setImagen] = useState(null);

  const [preview, setPreview] = useState("");

  const [editandoId, setEditandoId] = useState(null);

  const [busqueda, setBusqueda] = useState("");

  const [cargando, setCargando] = useState(true);

  const [guardando, setGuardando] = useState(false);

  const [mensaje, setMensaje] = useState(null);

  const [showDelete, setShowDelete] = useState(false);

  const [slideEliminar, setSlideEliminar] = useState(null);

  /* ============================================================
     TOKEN
  ============================================================ */

  const obtenerToken = () => {
    return sessionStorage.getItem("gj_admin_token");
  };

  /* ============================================================
     HEADERS
  ============================================================ */

  const authHeaders = () => ({
    Authorization: `Bearer ${obtenerToken()}`,
  });

  /* ============================================================
     MANEJAR ERROR DE AUTENTICACIÓN
  ============================================================ */

  const verificarSesion = (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      sessionStorage.removeItem("gj_admin_token");

      sessionStorage.removeItem("gj_admin_usuario");

      navigate("/login", {
        replace: true,
      });

      return true;
    }

    return false;
  };

  /* ============================================================
     MOSTRAR MENSAJE
  ============================================================ */

  const mostrarMensaje = (texto, tipo = "success") => {
    setMensaje({
      texto,
      tipo,
    });

    window.setTimeout(() => {
      setMensaje(null);
    }, 4000);
  };

  /* ============================================================
     CARGAR PORTADAS
  ============================================================ */

  const cargarSlides = async () => {
    try {
      setCargando(true);

      const respuesta = await axios.get(`${API}/admin/carousel`, {
        headers: authHeaders(),
        timeout: 15000,
      });

      setSlides(Array.isArray(respuesta.data) ? respuesta.data : []);
    } catch (error) {
      if (verificarSesion(error)) {
        return;
      }

      console.error("Error cargando carrusel:", error);

      mostrarMensaje("No se pudieron cargar las portadas.", "danger");
    } finally {
      setCargando(false);
    }
  };

  /* ============================================================
     CARGAR AL INICIAR
  ============================================================ */

  useEffect(() => {
    cargarSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================
     LIMPIAR URL DE PREVIEW
  ============================================================ */

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  /* ============================================================
     CAMBIAR CAMPOS
  ============================================================ */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ============================================================
     SELECCIONAR IMAGEN
  ============================================================ */

  const handleImagen = (e) => {
    const archivo = e.target.files?.[0];

    if (!archivo) {
      return;
    }

    const formatosPermitidos = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!formatosPermitidos.includes(archivo.type)) {
      mostrarMensaje("La imagen debe ser JPG, PNG o WEBP.", "danger");

      e.target.value = "";

      return;
    }

    const maximo = 8 * 1024 * 1024;

    if (archivo.size > maximo) {
      mostrarMensaje("La imagen no puede superar los 8 MB.", "danger");

      e.target.value = "";

      return;
    }

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    const nuevaPreview = URL.createObjectURL(archivo);

    setImagen(archivo);

    setPreview(nuevaPreview);
  };

  /* ============================================================
     QUITAR IMAGEN NUEVA
  ============================================================ */

  const quitarImagenSeleccionada = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setImagen(null);

    /*
     * Si estamos editando recuperamos la
     * imagen que ya tiene el registro.
     */
    if (editandoId) {
      const actual = slides.find((item) => item._id === editandoId);

      setPreview(actual?.imagen || "");
    } else {
      setPreview("");
    }

    if (inputImagenRef.current) {
      inputImagenRef.current.value = "";
    }
  };

  /* ============================================================
     NUEVA PORTADA / LIMPIAR
  ============================================================ */

  const limpiarFormulario = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setForm(formularioInicial);

    setImagen(null);

    setPreview("");

    setEditandoId(null);

    if (inputImagenRef.current) {
      inputImagenRef.current.value = "";
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     VALIDAR
  ============================================================ */

  const validarFormulario = () => {
    /*
     * Al crear es obligatorio seleccionar
     * una imagen.
     */
    if (!editandoId && !imagen) {
      mostrarMensaje("Selecciona una imagen para la portada.", "danger");

      return false;
    }

    const orden = Number(form.orden);

    if (Number.isNaN(orden) || orden < 0) {
      mostrarMensaje(
        "El orden debe ser un número igual o mayor a 0.",
        "danger",
      );

      return false;
    }

    return true;
  };

  /* ============================================================
     GUARDAR
  ============================================================ */

  const guardar = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    try {
      setGuardando(true);

      const datos = new FormData();

      datos.append("titulo", form.titulo.trim());

      datos.append("descripcion", form.descripcion.trim());

      datos.append("orden", String(Number(form.orden) || 0));

      datos.append("activo", String(form.activo));

      if (imagen) {
        datos.append("imagen", imagen);
      }

      /* ========================================================
         EDITAR
      ======================================================== */

      if (editandoId) {
        await axios.put(`${API}/admin/carousel/${editandoId}`, datos, {
          headers: {
            ...authHeaders(),
          },

          timeout: 30000,
        });

        mostrarMensaje("Portada actualizada correctamente.");
      } else {
        /* ========================================================
         CREAR
      ======================================================== */
        await axios.post(`${API}/admin/carousel`, datos, {
          headers: {
            ...authHeaders(),
          },

          timeout: 30000,
        });

        mostrarMensaje("Portada agregada correctamente.");
      }

      limpiarFormulario();

      await cargarSlides();
    } catch (error) {
      if (verificarSesion(error)) {
        return;
      }

      console.error("Error guardando portada:", error);

      mostrarMensaje(
        error?.response?.data?.message || "No se pudo guardar la portada.",
        "danger",
      );
    } finally {
      setGuardando(false);
    }
  };

  /* ============================================================
     EDITAR
  ============================================================ */

  const editar = (slide) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setEditandoId(slide._id);

    setForm({
      titulo: slide.titulo || "",

      descripcion: slide.descripcion || "",

      orden: slide.orden !== undefined ? slide.orden : 0,

      activo: slide.activo !== false,
    });

    setImagen(null);

    setPreview(slide.imagen || "");

    if (inputImagenRef.current) {
      inputImagenRef.current.value = "";
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     SOLICITAR ELIMINACIÓN
  ============================================================ */

  const solicitarEliminar = (slide) => {
    setSlideEliminar(slide);

    setShowDelete(true);
  };

  /* ============================================================
     CANCELAR ELIMINACIÓN
  ============================================================ */

  const cancelarEliminar = () => {
    setShowDelete(false);

    setSlideEliminar(null);
  };

  /* ============================================================
     ELIMINAR
  ============================================================ */

  const eliminar = async () => {
    if (!slideEliminar?._id) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/carousel/${slideEliminar._id}`, {
        headers: authHeaders(),
        timeout: 15000,
      });

      /*
       * Si estamos editando justamente
       * el elemento eliminado, limpiamos.
       */
      if (editandoId === slideEliminar._id) {
        limpiarFormulario();
      }

      cancelarEliminar();

      mostrarMensaje("Portada eliminada correctamente.");

      await cargarSlides();
    } catch (error) {
      if (verificarSesion(error)) {
        return;
      }

      console.error("Error eliminando portada:", error);

      mostrarMensaje(
        error?.response?.data?.message || "No se pudo eliminar la portada.",
        "danger",
      );
    }
  };

  /* ============================================================
     FILTRAR
  ============================================================ */

  const slidesFiltrados = slides.filter((slide) => {
    const texto = `${slide.titulo || ""} ${
      slide.descripcion || ""
    } ${slide.orden || ""}`.toLowerCase();

    return texto.includes(busqueda.toLowerCase().trim());
  });

  /* ============================================================
     CONTADORES
  ============================================================ */

  const totalActivos = slides.filter((item) => item.activo !== false).length;

  const totalInactivos = slides.length - totalActivos;

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="admin-productos-page">
      <div className="admin-productos-container">
        {/* =====================================================
            CABECERA
        ===================================================== */}

        <div className="admin-productos-header">
          <div>
            <div className="admin-productos-kicker">
              <Images className="me-2" />
              PORTADA PRINCIPAL
            </div>

            <h1>Administrar carrusel</h1>

            <p>
              Administra las imágenes principales que aparecen en la portada del
              sitio web.
            </p>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="me-2" />
              Panel administrativo
            </Button>

            <Button variant="primary" size="sm" onClick={limpiarFormulario}>
              <PlusCircle className="me-2" />
              Nueva portada
            </Button>
          </div>
        </div>

        {/* =====================================================
            MENSAJE
        ===================================================== */}

        {mensaje && (
          <Alert
            variant={mensaje.tipo}
            dismissible
            onClose={() => setMensaje(null)}
          >
            {mensaje.texto}
          </Alert>
        )}

        {/* =====================================================
            CONTADORES
        ===================================================== */}

        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="admin-productos-stat-card h-100">
              <Card.Body>
                <span>Total portadas</span>

                <strong>{slides.length}</strong>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="admin-productos-stat-card h-100">
              <Card.Body>
                <span>Activas</span>

                <strong>{totalActivos}</strong>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="admin-productos-stat-card h-100">
              <Card.Body>
                <span>Inactivas</span>

                <strong>{totalInactivos}</strong>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* =====================================================
            FORMULARIO
        ===================================================== */}

        <Card className="admin-productos-form-card mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
              <div>
                <span className="admin-productos-section-label">
                  {editandoId ? "EDITANDO PORTADA" : "NUEVA PORTADA"}
                </span>

                <h2 className="mb-0">
                  {editandoId
                    ? "Actualizar imagen del carrusel"
                    : "Agregar imagen al carrusel"}
                </h2>
              </div>

              {editandoId && (
                <Badge bg="warning" text="dark">
                  Modo edición
                </Badge>
              )}
            </div>

            <Form onSubmit={guardar}>
              <Row className="g-4">
                {/* =============================================
                    DATOS
                ============================================= */}

                <Col lg={5}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Título</Form.Label>

                        <Form.Control
                          type="text"
                          name="titulo"
                          value={form.titulo}
                          onChange={handleChange}
                          placeholder="Ej. G&J Frío Industrial"
                          maxLength={120}
                        />

                        <Form.Text muted>
                          Opcional. Puede utilizarse posteriormente sobre la
                          imagen.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Descripción</Form.Label>

                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="descripcion"
                          value={form.descripcion}
                          onChange={handleChange}
                          placeholder="Descripción opcional de la portada..."
                          maxLength={300}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Orden</Form.Label>

                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          name="orden"
                          value={form.orden}
                          onChange={handleChange}
                        />

                        <Form.Text muted>
                          Menor número = aparece primero.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Estado</Form.Label>

                        <div className="border rounded px-3 py-2">
                          <Form.Check
                            type="switch"
                            id="carousel-activo"
                            name="activo"
                            label={
                              form.activo ? "Portada activa" : "Portada oculta"
                            }
                            checked={form.activo}
                            onChange={handleChange}
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          Imagen de portada{" "}
                          {!editandoId && (
                            <span className="text-danger">*</span>
                          )}
                        </Form.Label>

                        <Form.Control
                          ref={inputImagenRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={handleImagen}
                        />

                        <Form.Text muted>
                          JPG, PNG o WEBP. Máximo 8 MB. Para una portada se
                          recomienda una imagen horizontal y de buena
                          resolución.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={guardando}
                        >
                          {guardando ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Save className="me-2" />

                              {editandoId
                                ? "Guardar cambios"
                                : "Guardar portada"}
                            </>
                          )}
                        </Button>

                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={limpiarFormulario}
                          disabled={guardando}
                        >
                          <ArrowClockwise className="me-2" />

                          {editandoId ? "Cancelar edición" : "Limpiar"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Col>

                {/* =============================================
                    VISTA PREVIA
                ============================================= */}

                <Col lg={7}>
                  <div className="h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">Vista previa</Form.Label>

                      {preview && (
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          onClick={quitarImagenSeleccionada}
                        >
                          <X className="me-1" />

                          {imagen ? "Quitar selección" : "Quitar vista"}
                        </Button>
                      )}
                    </div>

                    {preview ? (
                      <div
                        style={{
                          width: "100%",
                          minHeight: "280px",
                          border: "1px solid #dee2e6",
                          borderRadius: "12px",
                          overflow: "hidden",
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={preview}
                          alt="Vista previa de la portada"
                          style={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "360px",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          minHeight: "280px",
                          border: "1px dashed #adb5bd",
                          borderRadius: "12px",
                          background: "#f8f9fa",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#6c757d",
                          textAlign: "center",
                          padding: "30px",
                        }}
                      >
                        <ImageIcon size={38} className="mb-3" />

                        <strong>Sin imagen seleccionada</strong>

                        <span
                          style={{
                            fontSize: "13px",
                            marginTop: "5px",
                          }}
                        >
                          La portada aparecerá aquí antes de guardarla.
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* =====================================================
            LISTADO
        ===================================================== */}

        <Card className="admin-productos-list-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
              <div>
                <span className="admin-productos-section-label">
                  PORTADAS REGISTRADAS
                </span>

                <h2 className="mb-0">Carrusel principal</h2>
              </div>

              {/* BUSCADOR */}

              <div
                className="position-relative"
                style={{
                  width: "100%",
                  maxWidth: "330px",
                }}
              >
                <Search
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6c757d",
                  }}
                />

                <Form.Control
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar portada..."
                  style={{
                    paddingLeft: "38px",
                    paddingRight: busqueda ? "38px" : undefined,
                  }}
                />

                {busqueda && (
                  <button
                    type="button"
                    onClick={() => setBusqueda("")}
                    aria-label="Limpiar búsqueda"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: 0,
                      background: "transparent",
                      padding: 0,
                      color: "#6c757d",
                    }}
                  >
                    <X />
                  </button>
                )}
              </div>
            </div>

            {/* =================================================
                CARGANDO
            ================================================= */}

            {cargando ? (
              <div className="text-center py-5">
                <Spinner animation="grow" variant="primary" />

                <div className="mt-2 text-muted">Cargando portadas...</div>
              </div>
            ) : slidesFiltrados.length === 0 ? (
              /* ===============================================
                 VACÍO
              =============================================== */

              <div className="text-center py-5 text-muted">
                <Images size={36} className="mb-3" />

                <h5>No hay portadas para mostrar</h5>

                <p className="mb-0">
                  {busqueda
                    ? "No encontramos coincidencias con la búsqueda."
                    : "Agrega la primera imagen del carrusel principal."}
                </p>
              </div>
            ) : (
              /* ===============================================
                 TABLA
              =============================================== */

              <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: "150px",
                        }}
                      >
                        Imagen
                      </th>

                      <th>Información</th>

                      <th
                        className="text-center"
                        style={{
                          width: "90px",
                        }}
                      >
                        Orden
                      </th>

                      <th
                        className="text-center"
                        style={{
                          width: "110px",
                        }}
                      >
                        Estado
                      </th>

                      <th
                        className="text-end"
                        style={{
                          width: "210px",
                        }}
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {slidesFiltrados.map((slide) => (
                      <tr key={slide._id}>
                        {/* IMAGEN */}

                        <td>
                          <img
                            src={slide.imagen}
                            alt={slide.titulo || "Portada"}
                            loading="lazy"
                            decoding="async"
                            style={{
                              width: "130px",
                              height: "68px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #dee2e6",
                            }}
                          />
                        </td>

                        {/* INFORMACIÓN */}

                        <td>
                          <strong
                            style={{
                              color: "#253858",
                            }}
                          >
                            {slide.titulo || "Portada sin título"}
                          </strong>

                          {slide.descripcion && (
                            <div
                              className="text-muted mt-1"
                              style={{
                                fontSize: "12px",
                                maxWidth: "480px",
                              }}
                            >
                              {slide.descripcion}
                            </div>
                          )}
                        </td>

                        {/* ORDEN */}

                        <td className="text-center">
                          <Badge bg="light" text="dark">
                            {slide.orden ?? 0}
                          </Badge>
                        </td>

                        {/* ESTADO */}

                        <td className="text-center">
                          {slide.activo !== false ? (
                            <Badge bg="success">
                              <CheckCircleFill className="me-1" />
                              Activa
                            </Badge>
                          ) : (
                            <Badge bg="secondary">
                              <XCircleFill className="me-1" />
                              Oculta
                            </Badge>
                          )}
                        </td>

                        {/* ACCIONES */}

                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              title="Ver imagen"
                              onClick={() =>
                                window.open(
                                  slide.imagen,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              <EyeFill />
                            </Button>

                            <Button
                              type="button"
                              variant="outline-warning"
                              size="sm"
                              title="Editar"
                              onClick={() => editar(slide)}
                            >
                              <PencilSquare />
                            </Button>

                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              title="Eliminar"
                              onClick={() => solicitarEliminar(slide)}
                            >
                              <Trash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* =====================================================
            MODAL ELIMINAR
        ===================================================== */}

        <Modal show={showDelete} onHide={cancelarEliminar} centered>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar portada</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="mb-2">
              ¿Deseas eliminar esta portada del carrusel principal?
            </p>

            {slideEliminar && (
              <>
                {slideEliminar.imagen && (
                  <img
                    src={slideEliminar.imagen}
                    alt="Portada a eliminar"
                    style={{
                      width: "100%",
                      maxHeight: "190px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  />
                )}

                <strong>{slideEliminar.titulo || "Portada sin título"}</strong>
              </>
            )}

            <Alert variant="warning" className="mt-3 mb-0">
              La imagen también será eliminada de Cloudinary.
            </Alert>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={cancelarEliminar}>
              Cancelar
            </Button>

            <Button variant="danger" onClick={eliminar}>
              <Trash className="me-2" />
              Eliminar portada
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}
